import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicChrome";
import { useCart } from "@/context/CartContext";
import { Plus } from "lucide-react";

export default function ProductList() {
    const [products, setProducts] = useState([]);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [brandId, setBrandId] = useState(searchParams.get("brand") || "");
    const [categoryId, setCategoryId] = useState(searchParams.get("cat") || "");
    const [loading, setLoading] = useState(true);
    const { add } = useCart();

    const load = async () => {
        setLoading(true);
        const params = {};
        if (q) params.q = q;
        if (brandId) params.brand_id = brandId;
        if (categoryId) params.category_id = categoryId;
        const { data } = await api.get("/public/products", { params });
        setProducts(data);
        setLoading(false);
    };

    useEffect(() => {
        (async () => {
            const [b, c] = await Promise.all([api.get("/public/brands"), api.get("/public/categories")]);
            setBrands(b.data);
            setCategories(c.data);
            load();
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q, brandId, categoryId]);

    return (
        <div className="min-h-screen">
            <PublicHeader />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.24em] text-brand-blue">Catálogo · Prévia</div>
                        <h1 className="mt-2 font-heading font-black text-4xl tracking-tight">Produtos</h1>
                    </div>
                    <input
                        data-testid="search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar por nome, código ou descrição…"
                        className="w-full sm:w-96 h-12 px-4 bg-white border border-neutral-300 focus:outline-none focus:border-brand-blue"
                    />
                </div>

                <div className="grid lg:grid-cols-[240px,1fr] gap-8">
                    <aside className="space-y-6">
                        <div className="border border-neutral-300 bg-white">
                            <div className="p-4 border-b border-neutral-200 font-mono text-xs uppercase tracking-widest text-neutral-500 flex items-center justify-between">
                                <span>Marca</span>
                                <span className="text-neutral-400 normal-case tracking-normal">{brands.length}</span>
                            </div>
                            <div className="p-2 space-y-1 max-h-[420px] overflow-y-auto">
                                <button data-testid="filter-brand-all" onClick={() => setBrandId("")} className={`w-full text-left px-3 py-2 text-sm font-medium ${!brandId ? "bg-brand-blue text-white" : "hover:bg-neutral-100"}`}>Todas</button>
                                {brands.map((b) => (
                                    <button data-testid={`filter-brand-${b.slug}`} key={b.id} onClick={() => setBrandId(b.id)} className={`w-full text-left px-3 py-2 text-sm font-medium ${brandId === b.id ? "bg-brand-blue text-white" : "hover:bg-neutral-100"}`}>{b.name}</button>
                                ))}
                            </div>
                        </div>
                        <div className="border border-neutral-300 bg-white">
                            <div className="p-4 border-b border-neutral-200 font-mono text-xs uppercase tracking-widest text-neutral-500 flex items-center justify-between">
                                <span>Categoria</span>
                                <span className="text-neutral-400 normal-case tracking-normal">{categories.length}</span>
                            </div>
                            <div className="p-2 space-y-1 max-h-[420px] overflow-y-auto">
                                <button data-testid="filter-cat-all" onClick={() => setCategoryId("")} className={`w-full text-left px-3 py-2 text-sm font-medium ${!categoryId ? "bg-brand-blue text-white" : "hover:bg-neutral-100"}`}>Todas</button>
                                {categories.map((c) => (
                                    <button data-testid={`filter-cat-${c.slug}`} key={c.id} onClick={() => setCategoryId(c.id)} className={`w-full text-left px-3 py-2 text-sm font-medium ${categoryId === c.id ? "bg-brand-blue text-white" : "hover:bg-neutral-100"}`}>
                                        <span className="font-mono text-xs text-neutral-400 mr-2">{String(c.order || 0).padStart(2, "0")}</span>
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <div>
                        {loading ? (
                            <div className="font-mono text-sm text-neutral-500">carregando…</div>
                        ) : products.length === 0 ? (
                            <div className="border border-dashed border-neutral-300 p-16 text-center bg-white">
                                <div className="font-mono text-xs uppercase tracking-widest text-neutral-400">// nenhum produto encontrado</div>
                                <p className="mt-3 text-neutral-600">Ajuste os filtros ou aguarde novos itens no catálogo.</p>
                            </div>
                        ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
                                {products.map((p) => (
                                    <div key={p.id} data-testid={`product-card-${p.id}`} className="group bg-white border border-neutral-300 hover:border-brand-blue transition-colors flex flex-col relative">
                                        <Link to={`/produtos/${p.id}`} className="flex-1 flex flex-col">
                                            <div className="aspect-square bg-neutral-100 border-b border-neutral-200 overflow-hidden">
                                                {p.image_url ? (
                                                    <img src={fileUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                ) : (
                                                    <div className="w-full h-full grid place-items-center font-mono text-xs text-neutral-400">sem imagem</div>
                                                )}
                                            </div>
                                            <div className="p-4 flex-1 flex flex-col">
                                                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                                                    <span>{p.brand?.name || "—"}</span>
                                                    <span>{p.sku || "SKU—"}</span>
                                                </div>
                                                <div className="mt-2 font-heading font-bold text-lg leading-snug group-hover:text-brand-blue transition-colors">{p.name}</div>
                                                <div className="mt-1 text-xs text-neutral-500">{p.category?.name}</div>
                                            </div>
                                        </Link>
                                        <button
                                            data-testid={`add-to-list-${p.id}`}
                                            onClick={(e) => { e.preventDefault(); add(p); toast.success(`${p.name.slice(0,40)} adicionado`); }}
                                            className="border-t border-neutral-200 py-3 text-xs font-heading font-bold uppercase tracking-wider text-brand-blue hover:bg-brand-blue hover:text-white transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Plus size={14} /> Adicionar à Lista
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <PublicFooter />
        </div>
    );
}
